import React, { useState } from "react";
import { Box, Button, Grid, Input, Label, Notification, Select, Text } from "@telia/teddy";
import { Agent } from "../../../../../models/state";
import { post as Ajax_post } from "../../../app/Ajax";
import { API_URL_DEV_SKIP_CUSTOMER } from "../../api";
import pages from "../../../../../constants/pages";
import { DEV_MODE_TEST_USERS } from "./devModeTestUsers";
import "./DevModeStep.less";

interface Props {
    agent: Agent | null;
}

interface BufferResponse {
    bufferItem: {
        jsonData: string;
    };
}

interface BufferPersonData {
    firstName: string;
    lastName: string;
    ssn: string;
}

interface BufferAccountData {
    subscriptions?: Array<{
        person?: BufferPersonData;
    }>;
}

interface BufferNestedData {
    accounts?: BufferAccountData[];
}

const MANUAL_INPUT_VALUE = "manual-input";

const DevModeStep: React.FC<Props> = ({ agent }) => {
    const [ssn, setSsn] = useState("05097502856");
    const [firstName, setFirstName] = useState("Nela");
    const [lastName, setLastName] = useState("Torgersen");
    const [selectedTestUser, setSelectedTestUser] = useState("0");
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const isManualInput = selectedTestUser === MANUAL_INPUT_VALUE;
    const selectedUser = isManualInput ? undefined : DEV_MODE_TEST_USERS[Number(selectedTestUser)];
    const shouldFetchUserOnSubmit = !isManualInput && !!selectedUser?.url;

    const fetchBufferPerson = async (url: string, hasNoBAN?: boolean): Promise<BufferPersonData> => {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error("Klarte ikke å hente testbruker");
        }
        const data: BufferResponse = await response.json();
        const parsed = JSON.parse(data.bufferItem.jsonData) as BufferNestedData | BufferPersonData;
        // oxlint-disable-next-line no-console
        console.log("Fetched from test buffer:", JSON.stringify(parsed));

        const person = hasNoBAN
            ? (parsed as BufferPersonData)
            : (parsed as BufferNestedData)?.accounts?.[0]?.subscriptions?.[0]?.person;

        if (!person?.ssn || !person?.firstName || !person?.lastName) {
            throw new Error("Mangler persondata i buffer-svar");
        }

        return person;
    };

    const handleTestUserSelect = (index: string) => {
        if (index === MANUAL_INPUT_VALUE) {
            setErrorMessage(null);
            setSsn("");
            setFirstName("");
            setLastName("");
            return;
        }
        const user = DEV_MODE_TEST_USERS[Number(index)];
        if (!user) return;
        setErrorMessage(null);
        if (user.firstName && user.lastName && user.ssn) {
            setSsn(user.ssn);
            setFirstName(user.firstName);
            setLastName(user.lastName);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMessage(null);
        setIsLoading(true);
        try {
            let submitData = { ssn, firstName, lastName };
            if (selectedUser?.url) {
                const person = await fetchBufferPerson(selectedUser.url, selectedUser.hasNoBAN);
                submitData = {
                    ssn: person.ssn,
                    firstName: person.firstName,
                    lastName: person.lastName,
                };
                setSsn(person.ssn);
                setFirstName(person.firstName);
                setLastName(person.lastName);
            } else if (!isManualInput && selectedUser?.firstName && selectedUser?.lastName && selectedUser?.ssn) {
                submitData = {
                    ssn: selectedUser.ssn,
                    firstName: selectedUser.firstName,
                    lastName: selectedUser.lastName,
                };
            }

            const result = await Ajax_post<{ success: boolean; error?: string }>(
                {
                    url: API_URL_DEV_SKIP_CUSTOMER,
                    data: { ...submitData, agent, customerType: selectedUser?.customerType },
                },
                undefined,
            );
            if (result.success) {
                window.location.assign(pages.RETAILER.ORDER.SUBSCRIPTION.URL);
            } else {
                setErrorMessage(result.error || "Det oppsto en feil");
            }
        } catch (err: any) {
            setErrorMessage(err?.displayableMessage || err?.message || "Det oppsto en feil");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Box mt="200">
                <Label htmlFor="dev-test-user">Test user</Label>
                <Box mt="50">
                    <Select
                        className="dev-mode-test__user-select"
                        name="testUser"
                        options={[...DEV_MODE_TEST_USERS.map((_, i) => `${i}`), MANUAL_INPUT_VALUE]}
                        value={selectedTestUser}
                        onValueChange={(value: string) => {
                            setSelectedTestUser(value);
                            handleTestUserSelect(value);
                            return value;
                        }}
                    >
                        <Select.Trigger id="dev-test-user">
                            <Select.Value placeholder="Velg testbruker..." />
                        </Select.Trigger>
                        <Select.Portal>
                            <Select.Content>
                                <Select.Viewport>
                                    {DEV_MODE_TEST_USERS.map((user, i) => (
                                        <Select.Item key={i} value={`${i}`}>
                                            {user.label}
                                        </Select.Item>
                                    ))}
                                    <Select.Item value={MANUAL_INPUT_VALUE}>Manual input</Select.Item>
                                </Select.Viewport>
                            </Select.Content>
                        </Select.Portal>
                    </Select>
                </Box>
            </Box>
            {shouldFetchUserOnSubmit ? (
                <Box mt="200">
                    <Text>A test user will be fetched when you click continue</Text>
                    <Text mt="100" variant="additional-100" color="var(--teddy-theme-color-text-medium)">
                        (only use when you actually need a new user so we don't use up the test buffers)
                    </Text>
                </Box>
            ) : isManualInput ? (
                <>
                    <Box mt="200">
                        <Label htmlFor="dev-ssn">Fødselsnummer / D-nummer</Label>
                        <Input
                            id="dev-ssn"
                            type="text"
                            value={ssn}
                            onChange={(e) => setSsn(e.target.value)}
                            maxLength={11}
                            required
                            data-di-mask
                        />
                    </Box>
                    <Grid mt="200" columns="2" gap="200">
                        <Box>
                            <Label htmlFor="dev-first-name">Fornavn</Label>
                            <Input
                                id="dev-first-name"
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                                data-di-mask
                            />
                        </Box>
                        <Box>
                            <Label htmlFor="dev-last-name">Etternavn</Label>
                            <Input
                                id="dev-last-name"
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                required
                                data-di-mask
                            />
                        </Box>
                    </Grid>
                </>
            ) : null}
            {errorMessage && (
                <Notification type="tile" variant="error" mt="200">
                    <Notification.Icon />
                    <Notification.Text>{errorMessage}</Notification.Text>
                </Notification>
            )}
            <Button mt="300" type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
                Neste
            </Button>
        </form>
    );
};

export default DevModeStep;
